import React, { useState, useCallback } from 'react';

/**
 * PdfExportButton — A drop-in button that generates a PDF report from dashboard data.
 *
 * Props:
 *   onGenerate: async () => void  — Called when the button is clicked. The callback
 *     should use ReportGenerator to build and download the PDF. The button manages
 *     loading/error state automatically.
 *   label?: string — Button text (default: "Export PDF")
 *   disabled?: boolean — External disable control
 *   className?: string — Additional CSS class
 *
 * Usage:
 *   <PdfExportButton onGenerate={async () => {
 *     const report = new ReportGenerator('My Report', { from, to });
 *     report.addMetrics([...]);
 *     await report.addChartFromRef(chartRef, 'Chart Title');
 *     report.addTable(headers, rows);
 *     report.download();
 *   }} />
 */
export default function PdfExportButton({ onGenerate, label = 'Export PDF', disabled = false, className = '' }) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const handleClick = useCallback(async () => {
    if (generating || disabled) return;
    setGenerating(true);
    setError(null);
    try {
      await onGenerate();
    } catch (err) {
      console.error('[PdfExportButton] PDF generation failed:', err);
      setError('PDF generation failed');
      setTimeout(() => setError(null), 3000);
    } finally {
      setGenerating(false);
    }
  }, [onGenerate, generating, disabled]);

  return (
    <button
      className={`secondary ${className}`}
      onClick={handleClick}
      disabled={disabled || generating}
      title={error || ''}
      style={{
        position: 'relative',
        opacity: generating ? 0.7 : 1,
        cursor: generating ? 'wait' : disabled ? 'not-allowed' : 'pointer',
        minWidth: '110px',
        ...(error ? { borderColor: '#dc3545', color: '#dc3545' } : {}),
      }}
    >
      {generating ? (
        <>
          <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', marginRight: '6px' }}>⏳</span>
          Generating…
        </>
      ) : error ? (
        <>❌ {error}</>
      ) : (
        <>📄 {label}</>
      )}
    </button>
  );
}
